import { useRef, useState } from "react";
import type {
  InvoiceUploadInput,
  AcceptedAtPrice,
  ProposedDateAnswer,
  PurchaseOrderPage,
  ShortageInput,
} from "@shared-core/data/supplier-portal-adapter";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import {
  ACTION_WORDING,
  PAGE_WORDING,
  STAFF_VIEW_WORDING,
  needsYouWording,
  orderMeta,
  orderMetaShort,
  orderStatus,
  placedByLine,
} from "@shared-core/domain/supplier-portal-wording";
import { Button } from "../../app/components/primitives/button";
import { StatusPill } from "../../app/components/domain/StatusPill";
import {
  StaffViewReadOnly,
  StaffViewReadOnlyNoteOnce,
} from "../../app/components/domain/StaffViewReadOnly";
import { Banner, SectionHeading } from "../shell/states";
import { ActivityLog } from "./ActivityLog";
import { ArrivingSection } from "./ArrivingSection";
import { InvoiceSection } from "./InvoiceSection";
import {
  LineCard,
  despatchActionLabel,
  type LineForm,
  type LineWrites,
} from "./LineCard";

export interface PurchaseOrderWrites extends LineWrites {
  acceptAll: (lineNos: number[]) => Promise<void>;
  reportShortage: (input: ShortageInput) => Promise<void>;
  uploadInvoice: (input: InvoiceUploadInput) => Promise<void>;
  replyToQuery: (
    invoiceRef: string,
    input: { text: string; attachment: File | null },
  ) => Promise<void>;
}

/**
 * The purchase order page's body, the same by link and by login (brief §9): the header and status, the one
 * "Needs you" box, each line with its own actions (supplier orders map 1:1 to our lines), goods arriving for a
 * service order, the invoice, and the activity. `via` only changes the scope banner.
 */
export function PurchaseOrderBody({
  page,
  via,
  writes,
}: {
  page: PurchaseOrderPage;
  via: "link" | "login";
  writes: PurchaseOrderWrites;
}) {
  const [open, setOpen] = useState<{ lineNo: number; form: LineForm } | null>(
    null,
  );
  const [accepting, setAccepting] = useState<ReadonlySet<number>>(new Set());
  const [lineErrors, setLineErrors] = useState<Record<number, string>>({});
  const [proposedDateAnswers, setProposedDateAnswers] = useState<
    Record<number, ProposedDateAnswer>
  >({});
  const [priceAnswers, setPriceAnswers] = useState<
    Record<number, AcceptedAtPrice>
  >({});
  const [topError, setTopError] = useState<string | null>(null);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const linesRef = useRef<HTMLHeadingElement>(null);
  const invoiceRef = useRef<HTMLHeadingElement>(null);
  const lineRefs = useRef(new Map<number, HTMLDivElement>());

  const status = orderStatus(page);
  const placedBy = placedByLine(page);
  const isOpen = page.status !== "closed" && page.status !== "cancelled";
  const waiting = page.lines
    .filter((l) => l.actions.includes("accept"))
    .map((l) => l.line_no);

  const accept = async (lineNos: number[], where: "line" | "top") => {
    setAccepting(new Set(lineNos));
    setTopError(null);
    setLineErrors({});
    try {
      if (where === "top") await writes.acceptAll(lineNos);
      else await writes.accept(lineNos[0]);
    } catch (err) {
      const message = mutationErrorMessage(
        err,
        "We couldn't send your acceptance to Pollards.",
      );
      if (where === "top") setTopError(message);
      else setLineErrors({ [lineNos[0]]: message });
    } finally {
      setAccepting(new Set());
    }
  };

  const openDespatch = () => {
    const first = page.lines.find((l) => l.actions.includes("mark_despatched"));
    if (!first) return;
    setOpen({ lineNo: first.line_no, form: "mark_despatched" });
    lineRefs.current
      .get(first.line_no)
      ?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  const openInvoiceUpload = () => {
    setIsUploadOpen(true);
    invoiceRef.current?.scrollIntoView?.({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <article
      className="grid gap-4"
      aria-label={`Purchase order ${page.purchase_order_no}`}
    >
      {via === "link" && isOpen && (
        <Banner tone="info">
          {PAGE_WORDING.linkScope(page.purchase_order_no)}
        </Banner>
      )}

      <header className="grid gap-1">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <h1 className="text-xl font-bold tabular-nums">
            {page.purchase_order_no}
          </h1>
          <StatusPill label={status.label} tone={status.tone} />
          <p className="hidden text-sm text-muted-foreground sm:block">
            {orderMeta(page)}
          </p>
        </div>
        <p className="text-sm text-muted-foreground sm:hidden">
          {orderMetaShort(page)}
        </p>
        {placedBy && (
          <p className="text-sm text-muted-foreground">{placedBy}</p>
        )}
      </header>

      {page.status === "closed" && (
        <>
          <Banner tone="warn">
            {page.invoicing.can_upload && page.invoicing.upload_until
              ? PAGE_WORDING.closedBanner(page.invoicing.upload_until)
              : PAGE_WORDING.closedNoInvoice}
          </Banner>
          {page.invoicing.can_upload && !isUploadOpen && (
            <StaffViewReadOnly className="justify-self-start">
              <Button onClick={openInvoiceUpload}>
                {ACTION_WORDING.uploadInvoice}
              </Button>
            </StaffViewReadOnly>
          )}
        </>
      )}
      {page.status === "cancelled" && (
        <Banner tone="warn">{PAGE_WORDING.cancelledBanner}</Banner>
      )}
      <StaffViewReadOnlyNoteOnce sentence={STAFF_VIEW_WORDING.order} />

      {page.needs_you && (
        <NeedsYouBox
          page={page}
          error={topError}
          isAccepting={accepting.size > 0}
          onReviewLines={() =>
            linesRef.current?.scrollIntoView?.({
              behavior: "smooth",
              block: "start",
            })
          }
          onAcceptAll={() => void accept(waiting, "top")}
          onDespatch={openDespatch}
          onUploadInvoice={openInvoiceUpload}
        />
      )}

      <SectionHeading id="lines">
        <span ref={linesRef}>Lines</span>
      </SectionHeading>
      <div className="grid gap-3">
        {page.lines.map((line) => (
          <LineCard
            key={line.line_no}
            ref={(element) => {
              if (element) lineRefs.current.set(line.line_no, element);
              else lineRefs.current.delete(line.line_no);
            }}
            line={line}
            lines={page.lines}
            orderService={page.service}
            today={page.today}
            openForm={open?.lineNo === line.line_no ? open.form : null}
            onOpenForm={(form) =>
              setOpen(form ? { lineNo: line.line_no, form } : null)
            }
            isAccepting={accepting.has(line.line_no)}
            error={lineErrors[line.line_no] ?? null}
            proposedDateAnswer={proposedDateAnswers[line.line_no] ?? null}
            purchaseOrderNo={page.purchase_order_no}
            nextPriceIncrease={page.next_price_increase ?? null}
            acceptedAtPrice={priceAnswers[line.line_no] ?? null}
            writes={{
              ...writes,
              accept: (lineNo) => accept([lineNo], "line"),
              acceptAtPrice: async (lineNo, costPence) => {
                const answer = await writes.acceptAtPrice(lineNo, costPence);
                setPriceAnswers((answers) => ({
                  ...answers,
                  [lineNo]: answer,
                }));
                return answer;
              },
              proposeDate: async (lineNo, input) => {
                const answer = await writes.proposeDate(lineNo, input);
                setProposedDateAnswers((answers) => ({
                  ...answers,
                  [lineNo]: answer,
                }));
                return answer;
              },
            }}
          />
        ))}
      </div>

      {page.arriving.length > 0 && page.service && (
        <>
          <SectionHeading>Arriving for this job</SectionHeading>
          <ArrivingSection
            arriving={page.arriving}
            canReportShortage={page.can_report_shortage}
            onReportShortage={writes.reportShortage}
          />
        </>
      )}

      <SectionHeading>
        <span ref={invoiceRef}>Invoice</span>
      </SectionHeading>
      <InvoiceSection
        page={page}
        isUploadOpen={isUploadOpen}
        onUploadOpenChange={setIsUploadOpen}
        onUpload={writes.uploadInvoice}
        onReplyToQuery={writes.replyToQuery}
      />

      <SectionHeading>Activity</SectionHeading>
      <ActivityLog events={page.activity} service={page.service} />
    </article>
  );
}

function NeedsYouBox({
  page,
  error,
  isAccepting,
  onReviewLines,
  onAcceptAll,
  onDespatch,
  onUploadInvoice,
}: {
  page: PurchaseOrderPage;
  error: string | null;
  isAccepting: boolean;
  onReviewLines: () => void;
  onAcceptAll: () => void;
  onDespatch: () => void;
  onUploadInvoice: () => void;
}) {
  const needs = page.needs_you!;
  const { headline, detail } = needsYouWording(needs, page.service);
  return (
    <section
      aria-label="Needs you"
      className="flex flex-wrap items-center gap-2.5 rounded-lg border border-primary bg-primary/10 px-3.5 py-3"
    >
      <div className="min-w-0 flex-1 sm:min-w-[12rem]">
        <p className="font-semibold">{headline}</p>
        {detail && <p className="text-sm text-muted-foreground">{detail}</p>}
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
      </div>
      {needs.kind === "accept_lines" && (
        <>
          <Button
            variant="outline"
            className="hidden sm:inline-flex"
            onClick={onReviewLines}
          >
            {ACTION_WORDING.reviewLines}
          </Button>
          {page.can_accept_all && (
            <StaffViewReadOnly>
              <Button disabled={isAccepting} onClick={onAcceptAll}>
                {ACTION_WORDING.acceptAll}
              </Button>
            </StaffViewReadOnly>
          )}
        </>
      )}
      {needs.kind === "despatch" && (
        <StaffViewReadOnly>
          <Button onClick={onDespatch}>
            {despatchActionLabel(page.service)}
          </Button>
        </StaffViewReadOnly>
      )}
      {needs.kind === "upload_invoice" && (
        <StaffViewReadOnly>
          <Button onClick={onUploadInvoice}>
            {ACTION_WORDING.uploadInvoice}
          </Button>
        </StaffViewReadOnly>
      )}
    </section>
  );
}
